import FilterListIcon from "@mui/icons-material/FilterList";
import SortIcon from "@mui/icons-material/Sort";
import { Box, Checkbox, Chip, FormControlLabel } from "@mui/material";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SystemDropdown } from "../../../components/UIToolkit/Inputs/SystemDropDown/SystemDropdown";
import type { DropdownItem } from "../../../components/UIToolkit/Inputs/SystemDropDown/types";
import { BADGE } from "../../../theme/tokens";
import type { AlertSeverity, AlertSortMode, AlertType, DepartmentAlertsResponse } from "./types";
import {
	ALERT_TYPE_COLORS,
	ALERT_TYPE_I18N_KEYS,
	ALL_ALERT_TYPES,
	ALL_SEVERITIES,
	computeTotalAlertTypeCounts,
	SEVERITY_COLORS,
	SEVERITY_I18N_KEYS,
	SEVERITY_ICONS,
} from "./utils";

type Props = {
	data: DepartmentAlertsResponse | undefined;
	selectedTypes: Set<AlertType>;
	onToggleType: (type: AlertType) => void;
	selectedSeverities: Set<AlertSeverity>;
	onToggleSeverity: (sev: AlertSeverity) => void;
	sortMode: AlertSortMode;
	onSortChange: (mode: AlertSortMode) => void;
	showAll: boolean;
	onToggleAll: () => void;
	showPerTypeCount: boolean;
	onTogglePerTypeCount: () => void;
	showProblems: boolean;
	onToggleProblems: () => void;
};

export const AlertFilterBar: FC<Props> = ({
	data,
	selectedTypes,
	onToggleType,
	selectedSeverities,
	onToggleSeverity,
	sortMode,
	onSortChange,
	showAll,
	onToggleAll,
	showPerTypeCount,
	onTogglePerTypeCount,
	showProblems,
	onToggleProblems,
}) => {
	const { t } = useTranslation();

	const typeCounts = useMemo(() => computeTotalAlertTypeCounts(data?.days ?? []), [data?.days]);

	const sortItems: DropdownItem<AlertSortMode>[] = useMemo(
		() => [
			{ value: "newest", label: t("sort.newest") },
			{ value: "oldest", label: t("sort.oldest") },
			{ value: "most_alerts", label: t("sort.most.alerts") },
			{ value: "fewest_alerts", label: t("sort.fewest.alerts") },
		],
		[t],
	);

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: 1.5,
			}}
		>
			{/* Alert type filter chips */}
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					gap: 1,
					flexWrap: "wrap",
				}}
			>
				<FilterListIcon sx={{ fontSize: 18, color: "text.secondary" }} />

				<Chip
					label={t("filter.all")}
					variant={showAll ? "filled" : "outlined"}
					onClick={onToggleAll}
					sx={{
						fontWeight: showAll ? 700 : 400,
						bgcolor: showAll ? "grey.800" : undefined,
						color: showAll ? "white" : undefined,
						"&:hover": showAll ? { bgcolor: "grey.700" } : {},
					}}
				/>

				{ALL_ALERT_TYPES.map((type) => {
					const active = selectedTypes.has(type);
					const colors = ALERT_TYPE_COLORS[type];
					const count = typeCounts[type] ?? 0;
					return (
						<Chip
							key={type}
							label={
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 0.5,
									}}
								>
									{t(ALERT_TYPE_I18N_KEYS[type])}
									{count > 0 && (
										<Box
											component="span"
											sx={{
												bgcolor: active ? colors.text : "grey.400",
												color: "white",
												borderRadius: BADGE.PILL_RADIUS_FULL,
												px: 0.75,
												fontSize: "0.65rem",
												fontWeight: 700,
												lineHeight: 1.6,
												minWidth: 18,
												textAlign: "center",
											}}
										>
											{count}
										</Box>
									)}
								</Box>
							}
							variant={active ? "filled" : "outlined"}
							onClick={() => onToggleType(type)}
							sx={{
								fontWeight: active ? 600 : 400,
								bgcolor: active ? colors.chipBg : undefined,
								color: active ? colors.text : undefined,
								borderColor: active ? colors.text : undefined,
							}}
						/>
					);
				})}
			</Box>
			{/* Severity filter + sort */}
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
				}}
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1,
					}}
				>
					{ALL_SEVERITIES.map((sev) => {
						const active = selectedSeverities.has(sev);
						const colors = SEVERITY_COLORS[sev];
						const SevIcon = SEVERITY_ICONS[sev];
						return (
							<Chip
								key={sev}
								label={
									<Box
										sx={{
											display: "flex",
											alignItems: "center",
											gap: 0.5,
										}}
									>
										<SevIcon sx={{ fontSize: 14, color: colors.accent }} />
										{t(SEVERITY_I18N_KEYS[sev])}
									</Box>
								}
								variant="outlined"
								size="small"
								onClick={() => onToggleSeverity(sev)}
								sx={{
									fontWeight: active ? 600 : 400,
									bgcolor: active ? colors.lightBg : undefined,
									color: colors.text,
									borderColor: colors.accent,
								}}
							/>
						);
					})}
				</Box>

				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1,
					}}
				>
					<FormControlLabel
						control={<Checkbox checked={showProblems} onChange={onToggleProblems} size="small" />}
						label={t("show.problems")}
						slotProps={{ typography: { variant: "caption" } }}
					/>
					<FormControlLabel
						control={<Checkbox checked={showPerTypeCount} onChange={onTogglePerTypeCount} size="small" />}
						label={t("show.per.type")}
						slotProps={{ typography: { variant: "caption" } }}
					/>
					<SortIcon sx={{ fontSize: 18, color: "text.secondary" }} />
					<SystemDropdown
						items={sortItems}
						selectedItem={sortMode}
						onChange={(val) => val && onSortChange(val)}
						formControlSx={{ minWidth: 150 }}
						selectSx={{ fontSize: "0.8rem", bgcolor: "grey.50", "& .MuiSelect-select": { py: 0.75 } }}
					/>
				</Box>
			</Box>
		</Box>
	);
};
